import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { brand } from "../../../theme/businessFlowsTheme";
import { Refusal } from "../../workspaces/WorkspaceUi";
import { addViewType, reorderViewTypes, resetViewType, saveViewType, setViewTypeFlag } from "../../../actions";
import TypeIcon, { iconLabel } from "../../app/typeIcons";
import ColourSwatchPicker from "./ColourSwatchPicker";
import EmptyState from "./EmptyState";
import IconPicker from "./IconPicker";
import SortableList from "./SortableList";
import { colourName, nodeLabelProblem, relationshipProblem, suggestNodeLabel, suggestRelationship } from "./names";

const VISIBILITY = [
  { key: "inExplorer", label: "Explorer", where: "the explorer" },
  { key: "inSearch", label: "Search", where: "search" },
  { key: "inQuickSearch", label: "Quick search", where: "quick search" },
  { key: "inStructure", label: "Maps", where: "maps" },
];

// The five switches every type carries, in the order their columns appear.
// Each says what one change reads as, and what the same change for a whole
// column reads as, so a toast never says "Saved" and leaves the admin
// counting rows.
const COLUMNS = [
  ...VISIBILITY.map((flag) => ({
    ...flag,
    showAll: `Show every type in ${flag.label}`,
    hideAll: `Hide every type from ${flag.label}`,
    one: (label, on) => `${label} ${on ? "shown in" : "hidden from"} ${flag.where}.`,
    many: (count, on) => `${count} ${plural(count)} ${on ? "now" : "no longer"} ${count === 1 ? "shows" : "show"} in ${flag.where}.`,
    warning: (count) =>
      `${count} ${plural(count)} will no longer show in ${flag.where}. Users won't find ${count === 1 ? "it" : "them"} there until you switch ${
        count === 1 ? "it" : "them"
      } back on.`,
  })),
  {
    key: "editable",
    label: "Editable",
    where: null,
    showAll: "Let every type be created",
    hideAll: "Let no type be created",
    one: (label, on) => (on ? `${label} can now be created under its parents.` : `${label} can no longer be created.`),
    many: (count, on) =>
      on
        ? `${count} ${plural(count)} can now be created under ${count === 1 ? "its" : "their"} parents.`
        : `${count} ${plural(count)} can no longer be created.`,
    warning: (count) => `${count} ${plural(count)} will no longer be offered when users add content.`,
  },
];

// The columns line up with their header: one width for both.
const COLUMN_WIDTH = 74;
const COLUMN_GRID = { xs: "repeat(5, minmax(0, 1fr))", md: `repeat(5, ${COLUMN_WIDTH}px)` };
// What the row keeps for its preview, structure and reset buttons, so the
// switches end in the same place whether or not a type has been changed.
const ACTIONS_WIDTH = 104;
// The drag rail SortableList puts to the left of each row when it can be reordered.
const RAIL_WIDTH = 34;

// A type can only be made creatable once it says where it goes: the same rule
// the server applies, so the column control never sends a refusal.
const canBeCreated = (type) => !type.isDiagram && (type.parents || []).length > 0 && Boolean(type.relationship) && Boolean(type.nodeLabel);

const plural = (count) => (count === 1 ? "type" : "types");

// The types a column change leaves out, by the label the admin sees: up to
// five named, the rest counted, so a long list still fits in a toast.
const NAMED_AT_MOST = 5;
export const namedTypes = (types) => {
  const labels = types.map((type) => type.label || type.objectType);
  if (labels.length <= NAMED_AT_MOST) {
    return labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}` : labels.join("");
  }
  return `${labels.slice(0, NAMED_AT_MOST).join(", ")} and ${labels.length - NAMED_AT_MOST} more`;
};

const smallChip = { height: 20, fontSize: "0.7rem" };
const sameList = (a = [], b = []) => a.length === b.length && a.every((value, index) => value === b[index]);

// A switch for those who may change it; for everyone else, what it is set to.
function ReadOnlyFlag({ on, label, onText, offText }) {
  return (
    <Chip
      size="small"
      icon={on ? <CheckRoundedIcon /> : <CloseRoundedIcon />}
      label={`${label}: ${on ? onText : offText}`}
      variant={on ? "filled" : "outlined"}
      sx={{ ...smallChip, fontWeight: 600, mr: 0.5, mb: 0.5, bgcolor: on ? "rgba(15,123,67,0.12)" : "transparent" }}
    />
  );
}

function TypeRow({ type, overview, typeNames, database, mayManage, apply, askReset, selected, onPreview }) {
  const [label, setLabel] = useState(type.label);
  const [structureOpen, setStructureOpen] = useState(false);
  const [parents, setParents] = useState(type.parents || []);
  const [relationship, setRelationship] = useState(type.relationship || "");
  const [nodeLabel, setNodeLabel] = useState(type.nodeLabel || "");

  useEffect(() => setLabel(type.label), [type.label]);
  useEffect(() => {
    setParents(type.parents || []);
    setRelationship(type.relationship || "");
    setNodeLabel(type.nodeLabel || "");
  }, [type.parents, type.relationship, type.nodeLabel]);

  const save = (changes, message) => apply(() => saveViewType(database, type.objectType, changes), message);

  // A refused label goes back to the saved one, so the field never shows a
  // name users don't see.
  const commitLabel = async () => {
    const next = label.trim();
    if (!next) {
      setLabel(type.label);
      return;
    }
    if (next === type.label) return;
    const result = await save({ label: next }, `${type.objectType} is now shown as “${next}”.`);
    if (!result?.ok) setLabel(type.label);
  };

  const relationshipError = relationshipProblem(relationship);
  const nodeLabelError = nodeLabelProblem(nodeLabel);
  const structureDirty =
    !sameList(parents, type.parents || []) || relationship !== (type.relationship || "") || nodeLabel !== (type.nodeLabel || "");
  const parentOptions = [...new Set([...typeNames, ...parents])].filter((name) => name !== type.objectType);
  const stop = (e) => e.stopPropagation();

  return (
    <Paper
      elevation={0}
      onClick={() => onPreview(type.objectType)}
      sx={{
        p: 1.5,
        borderRadius: 3,
        border: `1px solid ${selected ? brand.ink : brand.line}`,
        transition: "border-color .15s ease",
        "&:hover": { borderColor: selected ? brand.ink : brand.inkSoft },
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {mayManage ? (
            <>
              <ColourSwatchPicker
                value={type.colour}
                palette={overview.palette}
                label={`Colour of ${type.label}`}
                onChange={(colour) => save({ colour }, `${type.label} has a new colour.`)}
              />
              <IconPicker
                value={type.icon}
                icons={overview.icons}
                colour={type.colour}
                label={`Icon of ${type.label}`}
                onChange={(icon) => save({ icon }, `${type.label} has a new icon.`)}
              />
            </>
          ) : (
            <>
              <Box
                role="img"
                aria-label={`Colour of ${type.label}: ${colourName(type.colour, overview.palette)}`}
                sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: type.colour, border: "2px solid #fff", boxShadow: `0 0 0 1px ${brand.line}`, flexShrink: 0 }}
              />
              <Box
                role="img"
                aria-label={`Icon of ${type.label}: ${iconLabel(type.icon)}`}
                sx={{ width: 32, height: 32, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: brand.canvas, color: type.colour || brand.ink, border: `1px solid ${brand.line}`, flexShrink: 0 }}
              >
                <TypeIcon name={type.icon} fontSize="small" />
              </Box>
            </>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {mayManage ? (
              <TextField
                size="small"
                fullWidth={false}
                value={label}
                onClick={stop}
                onChange={(e) => setLabel(e.target.value)}
                onBlur={commitLabel}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.querySelector("input")?.blur();
                }}
                inputProps={{ maxLength: 60, "aria-label": `Label for ${type.objectType}` }}
                sx={{ width: { xs: "100%", sm: 240 } }}
              />
            ) : (
              <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{type.label}</Typography>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
              {type.label !== type.objectType && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {type.objectType}
                </Typography>
              )}
              <Chip size="small" label={`${type.objects.toLocaleString()} ${type.objects === 1 ? "object" : "objects"}`} sx={smallChip} />
              {type.isDiagram && <Chip size="small" label="Diagram" sx={smallChip} />}
              {type.configured && <Chip size="small" label="Changed" sx={{ ...smallChip, bgcolor: "rgba(240,230,49,0.35)" }} />}
            </Stack>
          </Box>
        </Stack>

        {mayManage ? (
          // The same five columns as the header above, in the same widths, so
          // a row of switches reads down a column rather than across a wall.
          <Box onClick={stop} sx={{ display: "grid", gridTemplateColumns: COLUMN_GRID, columnGap: 0.5, justifyItems: "center", flexShrink: 0 }}>
            {COLUMNS.map((column) => (
              <Box key={column.key} sx={{ display: "grid", justifyItems: "center" }}>
                <Switch
                  size="small"
                  checked={Boolean(type[column.key])}
                  onChange={(e) => save({ [column.key]: e.target.checked }, column.one(type.label, e.target.checked))}
                  inputProps={{ "aria-label": `${column.label}: ${type.label}` }}
                />
                {/* Below the header's breakpoint each switch carries its own name. */}
                <Typography variant="caption" sx={{ display: { md: "none" }, fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}>
                  {column.label}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Stack direction="row" onClick={stop} sx={{ flexWrap: "wrap", columnGap: 0.5 }}>
            {VISIBILITY.map((flag) => (
              <ReadOnlyFlag key={flag.key} on={Boolean(type[flag.key])} label={flag.label} onText="shown" offText="hidden" />
            ))}
            <ReadOnlyFlag on={Boolean(type.editable)} label="Editable" onText="yes" offText="no" />
          </Stack>
        )}

        <Stack
          direction="row"
          spacing={0.5}
          onClick={stop}
          justifyContent="flex-end"
          sx={{ width: { md: ACTIONS_WIDTH }, flexShrink: 0 }}
        >
          <Tooltip title={selected ? "Shown in the preview" : "Preview"}>
            <IconButton
              size="small"
              aria-label={`Preview ${type.label}`}
              aria-pressed={selected}
              onClick={() => onPreview(type.objectType)}
              sx={{ bgcolor: selected ? brand.canvas : "transparent" }}
            >
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={structureOpen ? "Hide structure" : "Structure: where it can be created"}>
            <IconButton
              size="small"
              aria-expanded={structureOpen}
              aria-label={`Structure of ${type.label}`}
              onClick={() => setStructureOpen((open) => !open)}
              sx={{ bgcolor: structureOpen ? brand.canvas : "transparent" }}
            >
              <AccountTreeRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {type.configured && mayManage && (
            <Tooltip title="Back to the default">
              <IconButton
                size="small"
                aria-label={`Reset ${type.label}`}
                onClick={() =>
                  askReset({
                    title: `Reset ${type.label}?`,
                    text:
                      type.objects > 0
                        ? "Its label, colour, icon, where it is shown and its structure go back to the defaults. Its objects are not touched."
                        : `${type.objectType} has no objects yet, so going back to the default takes it off this list.`,
                    request: () => resetViewType(database, type.objectType),
                    message: `${type.objectType} is back to its defaults.`,
                  })
                }
              >
                <RestartAltRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Collapse in={structureOpen} unmountOnExit>
        {mayManage ? (
          <Box
            onClick={stop}
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px dashed ${brand.line}`,
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) auto" },
              alignItems: "start",
            }}
          >
            <Autocomplete
              multiple
              size="small"
              options={parentOptions}
              value={parents}
              onChange={(e, value) => setParents(value)}
              renderInput={(params) => <TextField {...params} label="Created under" helperText="Types an object of this type can be created under" />}
            />
            <TextField
              size="small"
              label="Relationship"
              value={relationship}
              error={Boolean(relationshipError)}
              onChange={(e) => setRelationship(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))}
              helperText={relationshipError || "Links it to its parent, e.g. PART_OF"}
              inputProps={{ maxLength: 60 }}
            />
            <TextField
              size="small"
              label="Database label"
              value={nodeLabel}
              error={Boolean(nodeLabelError)}
              onChange={(e) => setNodeLabel(e.target.value.replace(/[^A-Za-z0-9_]/g, ""))}
              helperText={nodeLabelError || "The label new objects get"}
              inputProps={{ maxLength: 60 }}
            />
            <Button
              variant="contained"
              disabled={!structureDirty || Boolean(relationshipError) || Boolean(nodeLabelError)}
              onClick={() => save({ parents, relationship: relationship || null, nodeLabel: nodeLabel || null }, `Structure of ${type.label} saved.`)}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Stack onClick={stop} spacing={0.5} sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${brand.line}` }}>
            <Typography variant="body2">
              <strong>Created under:</strong> {(type.parents || []).length ? type.parents.join(", ") : "not set"}
            </Typography>
            <Typography variant="body2">
              <strong>Relationship:</strong> {type.relationship || "not set"}
            </Typography>
            <Typography variant="body2">
              <strong>Database label:</strong> {type.nodeLabel || "not set"}
            </Typography>
          </Stack>
        )}
      </Collapse>
    </Paper>
  );
}

// What one column control would do to the types on screen: whether they are
// all on already, which ones would change, and which can't take the change.
export const plannedFor = (column, types) => {
  const allOn = types.length > 0 && types.every((type) => Boolean(type[column.key]));
  const value = !allOn;
  const wanted = types.filter((type) => Boolean(type[column.key]) !== value);
  // Only "Editable" has a condition of its own; a type without its structure
  // is left out rather than making the whole change fail, and is named so the
  // admin knows which ones to set up. A diagram type can never be created, so
  // it is neither sent nor named as something to set up.
  if (column.key !== "editable" || !value) return { allOn, value, targets: wanted, blocked: [] };
  const settable = wanted.filter((type) => !type.isDiagram);
  return { allOn, value, targets: settable.filter(canBeCreated), blocked: settable.filter((type) => !canBeCreated(type)) };
};

// The head of the five switch columns: what each column is, and one control
// that turns it on or off for every type the list is showing. It sits above
// the rows and lines up with them, so a column can be read downwards.
function ColumnHeader({ types, rail, onToggle }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-end"
      sx={{ display: { xs: "none", md: "flex" }, pl: `${rail}px`, px: 1.5, pb: 0.5 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Object type
        </Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: COLUMN_GRID, columnGap: 0.5, justifyItems: "center", flexShrink: 0 }}>
        {COLUMNS.map((column) => {
          const { allOn, targets, blocked } = plannedFor(column, types);
          const showing = !allOn;
          const name = showing ? column.showAll : column.hideAll;
          const nothingToDo = targets.length === 0;
          return (
            <Stack key={column.key} alignItems="center" spacing={0.25} sx={{ width: "100%" }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontSize: "0.68rem", lineHeight: 1.15, textAlign: "center", color: "text.secondary" }}
              >
                {column.label}
              </Typography>
              <Tooltip title={nothingToDo ? "No type here can be created yet - give it a parent type, a relationship and a database label first." : name}>
                <span>
                  <IconButton size="small" aria-label={name} disabled={nothingToDo} onClick={() => onToggle(column, showing, targets, blocked)}>
                    {showing ? <VisibilityRoundedIcon sx={{ fontSize: 18 }} /> : <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        })}
      </Box>
      <Box sx={{ width: ACTIONS_WIDTH, flexShrink: 0 }} />
    </Stack>
  );
}

const EMPTY_TYPE = { objectType: "", label: "", parents: [], relationship: "", nodeLabel: "" };

// "Create new structure": a type users can create, before any object has it.
// The server's refusal (a name taken, a label in use) is shown in the dialog.
function AddTypeDialog({ open, typeNames, onClose, onAdd }) {
  const [form, setForm] = useState(EMPTY_TYPE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setForm(EMPTY_TYPE);
      setError("");
    }
  }, [open]);

  const edit = (changes) => {
    setError("");
    setForm((f) => ({ ...f, ...changes }));
  };

  const name = form.objectType.trim();
  const relationship = form.relationship || suggestRelationship(name);
  const nodeLabel = form.nodeLabel || suggestNodeLabel(name);
  const relationshipError = relationshipProblem(form.relationship);
  const nodeLabelError = nodeLabelProblem(form.nodeLabel);
  // The server compares names ignoring case, so this does too.
  const existing = name ? typeNames.find((t) => t.toLowerCase() === name.toLowerCase()) : undefined;
  const ready =
    Boolean(name) &&
    !existing &&
    form.parents.length > 0 &&
    Boolean(relationship) &&
    Boolean(nodeLabel) &&
    !relationshipProblem(relationship) &&
    !nodeLabelProblem(nodeLabel);

  const submit = async () => {
    setBusy(true);
    setError("");
    const result = await onAdd({ objectType: name, ...(form.label.trim() ? { label: form.label.trim() } : {}), parents: form.parents, relationship, nodeLabel });
    setBusy(false);
    if (result && !result.ok) setError(result.msg);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Add an object type</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A new kind of object users can create in this database. Choose where it sits and how it links to its parent. It shows in the explorer,
          search and maps once objects exist.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Name"
            value={form.objectType}
            autoFocus
            required
            error={Boolean(existing)}
            onChange={(e) => edit({ objectType: e.target.value })}
            helperText={existing ? `“${existing}” already exists. Change it in the list instead.` : "Stored as the objects' Type, e.g. Risk"}
            inputProps={{ maxLength: 80 }}
          />
          <TextField label="Shown as (optional)" value={form.label} onChange={(e) => edit({ label: e.target.value })} inputProps={{ maxLength: 60 }} />
          <Autocomplete
            multiple
            options={typeNames}
            value={form.parents}
            onChange={(e, value) => edit({ parents: value })}
            renderInput={(params) => <TextField {...params} label="Created under" required helperText="At least one parent type" />}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Relationship"
              value={form.relationship}
              placeholder={suggestRelationship(name) || "RISK_OF"}
              error={Boolean(relationshipError)}
              onChange={(e) => edit({ relationship: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })}
              helperText={relationshipError || (form.relationship || !relationship ? "Letters, digits and _, e.g. RISK_OF" : `Leave empty to use ${relationship}`)}
              inputProps={{ maxLength: 60 }}
            />
            <TextField
              label="Database label"
              value={form.nodeLabel}
              placeholder={suggestNodeLabel(name) || "Risk"}
              error={Boolean(nodeLabelError)}
              onChange={(e) => edit({ nodeLabel: e.target.value.replace(/[^A-Za-z0-9_]/g, "") })}
              helperText={nodeLabelError || (form.nodeLabel || !nodeLabel ? "Letters, digits and _, e.g. Risk" : `Leave empty to use ${nodeLabel}`)}
              inputProps={{ maxLength: 60 }}
            />
          </Stack>
          {error && <Refusal>{error}</Refusal>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={!ready || busy} onClick={submit}>
          {busy ? "Adding…" : "Add type"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Every object type of the database: label, colour, icon, where it is shown,
// whether users can create it and where, and its order in filters and legends.
export default function ObjectTypesTab({ overview, database, mayManage, apply, askReset, previewType, onPreview }) {
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const types = useMemo(() => overview.types || [], [overview.types]);
  const typeNames = useMemo(() => types.map((t) => t.objectType), [types]);
  const term = query.trim().toLowerCase();
  const filtered = term ? types.filter((t) => `${t.label} ${t.objectType}`.toLowerCase().includes(term)) : types;

  const reorder = (next) => apply(() => reorderViewTypes(database, next.map((t) => t.objectType)), "Order saved. Filters and legends follow it.");

  // A whole column at once, for the types the list is showing - so a search
  // narrows what it touches. One request, however long the list, and turning
  // something off for everyone is confirmed first: it can empty a view.
  const toggleColumn = (column, value, targets, blocked) => {
    const names = targets.map((t) => t.objectType);
    const skipped = blocked.length;
    const left = skipped
      ? ` ${skipped} ${plural(skipped)} still ${skipped === 1 ? "needs" : "need"} a parent type, a relationship and a database label: ${namedTypes(blocked)}.`
      : "";
    const message = `${column.many(names.length, value)}${left}`;
    const request = () => setViewTypeFlag(database, column.key, value, names);
    // Some types would be left out of "Editable": say which before anything
    // changes, so the admin can set those up first instead of finding out after.
    if (value && skipped) {
      askReset({
        title: `${column.showAll}?`,
        text: `${names.length} ${plural(names.length)} will be offered when users add content. ${skipped} ${plural(skipped)} ${
          skipped === 1 ? "is" : "are"
        } left out until ${skipped === 1 ? "it has" : "they have"} a parent type, a relationship and a database label: ${namedTypes(blocked)}.`,
        confirmLabel: `Make ${names.length === 1 ? "it" : "them"} editable`,
        confirmIcon: <CheckRoundedIcon />,
        request,
        message,
      });
      return;
    }
    if (value) {
      apply(request, message);
      return;
    }
    askReset({
      title: `${column.hideAll}?`,
      text: column.warning(names.length),
      confirmLabel: column.key === "editable" ? "Turn it off for all" : "Hide them all",
      confirmIcon: <VisibilityOffRoundedIcon />,
      request,
      message,
    });
  };

  const add = async (body) => {
    const result = await apply(() => addViewType(database, body), `${body.objectType} added. It can now be created under its parents.`, { inline: true });
    if (result.ok) setAdding(false);
    return result;
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <TextField
          size="small"
          placeholder="Find a type"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ "aria-label": "Find a type" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, "& .MuiInputBase-root": { bgcolor: "#fff" } }}
        />
        {mayManage && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setAdding(true)} sx={{ flexShrink: 0 }}>
            Add object type
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Every type in the database is shown everywhere until you switch it off. A control at the head of each column switches it for every type in
        the list below - narrow the list first to change only some. Drag to set the order of filters and legends.
      </Typography>

      {types.length === 0 ? (
        <EmptyState
          icon={<CategoryRoundedIcon />}
          title="This database has no objects yet"
          text="Import content, or add an object type so users can create the first objects."
          action={
            mayManage ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setAdding(true)}>
                Add object type
              </Button>
            ) : null
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<SearchRoundedIcon />} title="No types match" text="Try another name, or clear the search." />
      ) : (
        <>
        {mayManage && <ColumnHeader types={filtered} rail={term ? 0 : RAIL_WIDTH} onToggle={toggleColumn} />}
        <SortableList
          items={filtered}
          getKey={(t) => t.objectType}
          getLabel={(t) => t.label}
          disabled={!mayManage || Boolean(term)}
          onReorder={reorder}
          renderItem={(t) => (
            <TypeRow
              type={t}
              overview={overview}
              typeNames={typeNames}
              database={database}
              mayManage={mayManage}
              apply={apply}
              askReset={askReset}
              selected={previewType === t.objectType}
              onPreview={onPreview}
            />
          )}
        />
        </>
      )}

      <AddTypeDialog open={adding} typeNames={typeNames} onClose={() => setAdding(false)} onAdd={add} />
    </Stack>
  );
}
